import {createFileRoute, useParams} from '@tanstack/react-router';
import {useQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {apiActionImproved, apiRetrieveImproved} from '../api';
import {useJwtAuth} from '../JWTAuthProvider';
import {GradeToText} from '../constants';
import {CountriesMap} from '../components/PSelectCountry';
import {codeSentBy, offersTextInstead} from '../components/codeChannel';

/**
 * The shareable claim page — /join/{league}/{token}, the link an organizer drops in the club
 * group chat.
 *
 * The player's whole journey: tap link → pick your name → phone number → OTP code → done. No
 * password, no email, no app install, finishable one-handed on a phone — because Path A counts an
 * entrant only once they hold a real account with a VERIFIED phone, and every extra field on this
 * page is a player who never finishes.
 *
 * The token is the league's share HMAC (League::shareClaimToken). Possession proves the link came
 * from the organizer; the OTP proves the phone. Only placeholder (InvitedByEmail) spots are
 * claimable — the server refuses anything else, so nobody's real account can be picked off a list.
 */
export const Route = createFileRoute('/join/$event_uid/$token')({
    component: JoinView,
});

type Entrant = {uid: string; name: string; claimed: boolean; claimable: boolean};

function JoinView() {
    const params = useParams({strict: false}) as {event_uid: string; token: string};
    const jwt = useJwtAuth();
    const [step, setStep] = useState<'pick' | 'phone' | 'code' | 'questionnaire' | 'done'>('pick');
    const [picked, setPicked] = useState<Entrant | null>(null);
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    // Where the code went: the mobile the organizer has on file wins over the number typed.
    const [sentTo, setSentTo] = useState('');
    // How the code went: 'whatsapp' first, 'sms' when WhatsApp could not take it or they asked.
    const [channel, setChannel] = useState('sms');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The signup questionnaire — name and nationality first, then game questions. The initial
    // RATING seeds from the event's level server-side (a player enrolling into a C-grade league
    // starts at that level); the self-declared grade is the fallback when the event has none.
    const [fullName, setFullName] = useState('');
    const [nationality, setNationality] = useState('');
    const [grade, setGrade] = useState('C+');
    const [howOften, setHowOften] = useState('weekly');

    const joinQuery = useQuery({
        queryKey: ['event-join', params.event_uid, params.token],
        queryFn:  () => apiRetrieveImproved('event-public', {
            uid:         params.event_uid,
            share_token: params.token,
        }),
        retry:    false,
        enabled:  !!params.event_uid && !!params.token,
    });

    const isTournament = joinQuery.data?.type === 'tournament_join';
    // A player grade is a racquet concept: an esports or football event never asks for one.
    const isEsports = ['esports', 'football'].includes(String(joinQuery.data?.sport_type || ''));
    // The claim endpoint routes on which uid key is present -- league_uid or tournament_uid.
    const eventKey = isTournament ? {tournament_uid: params.event_uid} : {league_uid: params.event_uid};
    const entrants: Entrant[] = joinQuery.data?.entrants ?? [];
    const claimedCount = entrants.filter((e) => e.claimed).length;

    const sendCode = async (textInstead = false) => {
        setBusy(true); setError(null);
        try {
            const res: any = await apiActionImproved('invite-claim', {
                ...eventKey,
                share_token: params.token,
                player_uid:  picked!.uid,
                phone,
                ...(textInstead ? {channel: 'sms'} : {}),
            });
            setChannel(res?.channel || 'sms');
            setSentTo(res?.phone_on_file ? `the mobile your organizer has for you (${res.phone_hint})` : phone);
            setStep('code');
        } catch (e: any) {
            setError(e?.message || 'Could not send the code. Check the number and try again.');
        } finally {
            setBusy(false);
        }
    };

    const verifyAndClaim = async () => {
        setBusy(true); setError(null);
        try {
            const res: any = await apiActionImproved('invite-claim', {
                ...eventKey,
                share_token: params.token,
                player_uid:  picked!.uid,
                phone,
                code,
            });
            if (res?.error || !res?.access_token) {
                throw new Error(res?.error || res?.message || 'That code did not work. Try again.');
            }
            // The claim logs the player in — keep the session so "see the league" just works,
            // exactly as the email claim page does.
            jwt.setFieldsFromResponse(res);
            setStep('questionnaire');
        } catch (e: any) {
            setError(e?.message || 'That code did not work. Try again.');
        } finally {
            setBusy(false);
        }
    };

    if (joinQuery.isError) {
        return (
            <Shell>
                <p className="text-lg font-semibold">This invite link isn&rsquo;t valid</p>
                <p className="mt-2 text-sm text-white/60">
                    It may be out of date — ask your organizer to send the link again.
                </p>
            </Shell>
        );
    }

    if (joinQuery.isLoading) {
        return <Shell><p className="text-sm text-white/60">Loading…</p></Shell>;
    }

    return (
        <Shell>
            <p className="text-xs uppercase tracking-widest text-white/40">You&rsquo;re invited to join</p>
            <h1 className="mt-1 text-2xl font-bold">{joinQuery.data?.title}</h1>

            {step === 'pick' && (
                <>
                    <p className="mt-4 text-sm text-white/70">Which player are you?</p>
                    <div className="mt-3 flex flex-col gap-2">
                        {entrants.map((e) => (
                            <button
                                key={e.uid}
                                type="button"
                                disabled={!e.claimable}
                                onClick={() => { setPicked(e); setStep('phone'); }}
                                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-base ${
                                    e.claimable
                                        ? 'border-white/25 bg-white/5 active:bg-white/15'
                                        : 'border-white/10 text-white/35'
                                }`}
                            >
                                <span>{e.name || 'Unnamed player'}</span>
                                {e.claimed && <span className="text-xs text-emerald-400">✓ already in</span>}
                                {!e.claimed && !e.claimable && <span className="text-xs text-white/30">has an account — log in</span>}
                            </button>
                        ))}
                        {entrants.length === 0 && (
                            <p className="text-sm text-white/50">No players are listed yet — ask your organizer.</p>
                        )}
                    </div>
                    {claimedCount > 0 && (
                        <p className="mt-4 text-xs text-white/40">{claimedCount} of {entrants.length} already in.</p>
                    )}
                </>
            )}

            {step === 'phone' && picked && (
                <>
                    <p className="mt-4 text-sm text-white/70">
                        Hi <strong className="text-white">{picked.name}</strong> — confirm your phone number and
                        we&rsquo;ll text you a code. That&rsquo;s it: no password, no email.
                    </p>
                    <input
                        type="tel"
                        inputMode="tel"
                        autoFocus
                        placeholder="+971 50 123 4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="mt-4 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-lg outline-none focus:border-white/60"
                    />
                    <p className="mt-1 text-xs text-white/40">Include your country code, e.g. +971…</p>
                    {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
                    <button type="button" onClick={() => sendCode()} disabled={busy || phone.trim().length < 7}
                            className="mt-4 w-full rounded-full bg-white py-3 text-base font-semibold text-black disabled:opacity-50">
                        {busy ? 'Sending…' : 'Text me the code'}
                    </button>
                    <button type="button" onClick={() => { setStep('pick'); setError(null); }}
                            className="mt-2 w-full py-2 text-sm text-white/50">
                        Not you? Pick a different name
                    </button>
                </>
            )}

            {step === 'code' && picked && (
                <>
                    <p className="mt-4 text-sm text-white/70">Enter the code we sent {codeSentBy(channel)} to <strong className="text-white">{sentTo || phone}</strong>.</p>
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        maxLength={8}
                        placeholder="123456"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="mt-4 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-center text-2xl tracking-[0.4em] outline-none focus:border-white/60"
                    />
                    {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
                    <button type="button" onClick={verifyAndClaim} disabled={busy || code.replace(/\D/g, '').length < 4}
                            className="mt-4 w-full rounded-full bg-white py-3 text-base font-semibold text-black disabled:opacity-50">
                        {busy ? 'Checking…' : "I'm in"}
                    </button>
                    {offersTextInstead(channel) && (
                        <button type="button" onClick={() => sendCode(true)} disabled={busy}
                                className="mt-2 w-full py-2 text-sm font-semibold text-white/80 underline">
                            Text me instead
                        </button>
                    )}
                    <button type="button" onClick={() => sendCode()} disabled={busy}
                            className="mt-2 w-full py-2 text-sm text-white/50">
                        Send a new code
                    </button>
                </>
            )}

            {step === 'questionnaire' && picked && (
                <>
                    <p className="mt-4 text-sm text-white/70">
                        Number confirmed, <strong className="text-white">{picked.name}</strong>! A few quick
                        details and your account is complete.
                    </p>

                    <label className="mt-4 block text-xs uppercase tracking-wider text-white/40">Your name</label>
                    <input type="text" autoComplete="name" placeholder={picked.name || 'Your full name'}
                           value={fullName} onChange={(e) => setFullName(e.target.value)}
                           className="mt-1 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-base text-white outline-none focus:border-white/60" />

                    <label className="mt-4 block text-xs uppercase tracking-wider text-white/40">Nationality</label>
                    <select value={nationality} onChange={(e) => setNationality(e.target.value)}
                            className="mt-1 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-base text-white outline-none focus:border-white/60 [&>option]:text-black">
                        <option value="">Choose…</option>
                        {Object.entries(CountriesMap).map(([code, name]) => (
                            <option key={code} value={code}>{name as string}</option>
                        ))}
                    </select>

                    {/* A player grade is a racquet concept — an esports event never asks for one. */}
                    {!isEsports && <>
                        <label className="mt-4 block text-xs uppercase tracking-wider text-white/40">What player grade are you?</label>
                        <select value={grade} onChange={(e) => setGrade(e.target.value)}
                                className="mt-1 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-base text-white outline-none focus:border-white/60 [&>option]:text-black">
                            {['A', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'Beginner'].map((g) => (
                                <option key={g} value={g}>{GradeToText(g, joinQuery.data?.sport_type)}</option>
                            ))}
                        </select>
                    </>}

                    <label className="mt-4 block text-xs uppercase tracking-wider text-white/40">How often do you play?</label>
                    <select value={howOften} onChange={(e) => setHowOften(e.target.value)}
                            className="mt-1 w-full rounded-xl border border-white/25 bg-white/5 px-4 py-3 text-base text-white outline-none focus:border-white/60 [&>option]:text-black">
                        <option value="rarely">Very rarely</option>
                        <option value="monthly">Once a month</option>
                        <option value="bimonthly">Two times a month</option>
                        <option value="weekly">Every week</option>
                        <option value="biweekly">Twice per week</option>
                        <option value="often">Three times per week or more</option>
                    </select>

                    {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
                    <button type="button" disabled={busy || !fullName.trim()}
                            onClick={async () => {
                                setBusy(true); setError(null);
                                try {
                                    await apiActionImproved('invite-claim', {
                                        ...eventKey,
                                        share_token:   params.token,
                                        player_uid:    picked.uid,
                                        questionnaire: {name: fullName.trim(), nationality: nationality || undefined, ...(isEsports ? {} : {elo_grade: grade}), how_often: howOften},
                                    });
                                    setStep('done');
                                } catch (e: any) {
                                    setError(e?.message || 'Could not save your answers. Try again.');
                                } finally {
                                    setBusy(false);
                                }
                            }}
                            className="mt-5 w-full rounded-full bg-white py-3 text-base font-semibold text-black disabled:opacity-50">
                        {busy ? 'Saving…' : 'Complete my account'}
                    </button>
                </>
            )}

            {step === 'done' && picked && (
                <>
                    <p className="mt-6 text-4xl">🎉</p>
                    <p className="mt-2 text-lg font-semibold">You&rsquo;re in, {picked.name}.</p>
                    <p className="mt-2 text-sm text-white/60">
                        Your account is yours now and your number is verified — you count towards keeping
                        this {isTournament ? 'event' : 'league'} free for everyone. Next time, sign in with
                        your phone number — no password needed.
                    </p>
                    <a href="/profiles/me/"
                       className="mt-5 inline-block w-full rounded-full bg-white py-3 text-center text-base font-semibold text-black">
                        Go to Profile
                    </a>
                    <a href={`/e/${params.event_uid}`}
                       className="mt-2 inline-block w-full py-2 text-center text-sm text-white/50">
                        See the {isTournament ? 'event' : 'league'}
                    </a>
                </>
            )}
        </Shell>
    );
}

function Shell({children}: {children: React.ReactNode}) {
    return (
        <div className="min-h-screen bg-[#0a0e1a] px-5 py-10 font-inter text-white">
            <div className="mx-auto w-full max-w-sm">{children}</div>
        </div>
    );
}
